import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Warning, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import type { AvailabilityBlockReason } from '@/components/schedule/ScheduleCalendar';

// AvailabilityBlockNotice surfaces the reason a calendar interaction
// (click-to-create, drop, resize) was rejected by the availability gate.
// It auto-dismisses after a short window so it doesn't stick around
// past the moment it's useful, and re-triggers cleanly if the user
// hits another blocked interaction before the previous one cleared
// (the `key` carries a timestamp so a fresh notice re-runs the effect).
//
// Lives next to the calendar (parent uses `relative` positioning) — the
// notice is `absolute` so it doesn't push the calendar down when it
// pops in.

const DISMISS_AFTER_MS = 4000;

interface NoticeState {
  reason: AvailabilityBlockReason;
  // Monotonic timestamp so a back-to-back block retriggers the auto-
  // dismiss timer (React's useEffect re-runs when the dep changes).
  key: number;
}

export function useAvailabilityBlockNotice() {
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const onBlocked = useCallback((reason: AvailabilityBlockReason) => {
    setNotice({ reason, key: Date.now() });
  }, []);

  const clear = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), DISMISS_AFTER_MS);
    return () => clearTimeout(t);
  }, [notice]);

  return { notice, onBlocked, clear };
}

interface Props {
  reason: AvailabilityBlockReason;
  onDismiss: () => void;
}

// The notice is rendered as a `fixed` toast at the bottom of the viewport
// so it's the same affordance regardless of which calendar (schedule or
// template) triggered it. Amber palette signals "warning, here's why"
// without competing with the lime primary or the destructive red.
// tailwindcss-animate ships the `animate-in / slide-in-from-bottom`
// utilities; the duration matches the design system's micro-interaction
// timing elsewhere.
export function AvailabilityBlockNotice({ reason, onDismiss }: Props) {
  const { t } = useTranslation();
  const message = t(`schedule.blocked.${reason}` as const);
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed bottom-6 left-1/2 z-50 flex w-[min(90vw,28rem)] -translate-x-1/2 items-center gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 px-5 py-3 text-sm font-medium text-amber-950 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 dark:border-amber-700/60 dark:bg-amber-950/85 dark:text-amber-50"
    >
      <span
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-amber-200/70 text-amber-700 dark:bg-amber-800/50 dark:text-amber-200"
      >
        <Warning weight="fill" size={20} />
      </span>
      <span className="min-w-0 flex-1 leading-snug">{message}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('common.cancel')}
        onClick={onDismiss}
        className="-mr-1 h-7 w-7 shrink-0 text-amber-900/70 hover:bg-amber-900/10 dark:text-amber-100/70 dark:hover:bg-amber-100/10"
      >
        <X weight="bold" size={14} />
      </Button>
    </div>
  );
}
